import { useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export interface TrendPoint {
  /** Milliseconds from the start of the series — the x scale. */
  t: number;
  value: number;
  label: string;
}

interface Props {
  points: TrendPoint[];
  /** Series name. A single series needs no legend box; the title names it. */
  title: string;
  /** Rendered next to the hovered value, e.g. 'km/h'. */
  unit: string;
  /** Formats the y-axis bounds and the tooltip figure. */
  format: (value: number) => string;
  /** Optional horizontal reference, e.g. the day's average. */
  reference?: { value: number; label: string };
  /**
   * Milliseconds after which two consecutive readings are a hole in the record
   * rather than a trend, and the line is broken instead of joined.
   *
   * Without it this chart drew a clean diagonal across a 1h 41m outage — a
   * smooth acceleration to 42 km/h that nobody recorded and that reads exactly
   * like data. The map already refuses to join a gap with a solid line
   * (docs/05-TRACKING-ENGINE.md §9); a chart that quietly does it anyway is the
   * same misrepresentation with better styling.
   */
  maxGapMs?: number;
  tone?: 'moving' | 'default';
  className?: string;
  height?: number;
}

const VIEW_W = 100;

/**
 * A single-series trend with a crosshair.
 *
 * Deliberately thin: a 2px line over a soft fill, hairline axes, no gridlines
 * fighting the data, and no number printed on every point — the crosshair
 * carries the value the reader actually asked for by pointing at it, and the
 * two axis bounds carry the range.
 *
 * The x scale is real elapsed time, not the point index. Location fixes arrive
 * irregularly — every 30 s while driving, once every few minutes while parked —
 * so plotting them evenly spaced would stretch the stationary hours across half
 * the chart and compress the drive that matters into a corner.
 */
export function Trend({
  points,
  title,
  unit,
  format,
  reference,
  maxGapMs,
  tone = 'default',
  className,
  height = 96,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const model = useMemo(() => {
    const first = points[0];
    const last = points[points.length - 1];
    if (!first || !last || points.length < 2) return null;

    const span = Math.max(1, last.t - first.t);
    const max = Math.max(...points.map((p) => p.value), reference?.value ?? 0);
    // Zero-based: a speed chart whose floor is 12 km/h invents a dramatic
    // collapse every time someone slows to 15.
    const top = max <= 0 ? 1 : max * 1.1;

    const scaled = points.map((p) => ({
      ...p,
      x: ((p.t - first.t) / span) * VIEW_W,
      y: 100 - (p.value / top) * 100,
    }));

    // Split at gaps, so each run is drawn as its own path and the space between
    // runs stays empty. One `M` per run is what makes the break visible rather
    // than papered over with a straight line.
    const runs: (typeof scaled)[] = [];
    let run: typeof scaled = [];
    for (let i = 0; i < scaled.length; i++) {
      const current = scaled[i];
      const previous = scaled[i - 1];
      if (!current) continue;
      const broken =
        maxGapMs !== undefined && previous !== undefined && current.t - previous.t > maxGapMs;
      if (broken && run.length > 0) {
        runs.push(run);
        run = [];
      }
      run.push(current);
    }
    if (run.length > 0) runs.push(run);

    const path = (r: typeof scaled) =>
      r.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');

    const lines = runs.filter((r) => r.length > 1).map(path);
    // The fill closes to the baseline per run, so a gap leaves a genuine hole
    // in the area rather than a wedge spanning it.
    const areas = runs
      .filter((r) => r.length > 1)
      .map((r) => {
        const startX = r[0]?.x.toFixed(2) ?? '0';
        const endX = r[r.length - 1]?.x.toFixed(2) ?? '0';
        return `${path(r)} L${endX},100 L${startX},100 Z`;
      });

    return { scaled, lines, areas, top, span, first, gapCount: runs.length - 1 };
  }, [points, reference?.value, maxGapMs]);

  if (!model) {
    return (
      <p className={cn('py-6 text-center text-sm text-muted-foreground', className)}>
        Not enough data to chart.
      </p>
    );
  }

  const stroke = tone === 'moving' ? 'stroke-track-moving' : 'stroke-primary';
  const fill = tone === 'moving' ? 'text-track-moving' : 'text-primary';
  const active = hover === null ? null : model.scaled[hover];

  const onMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    const target = ratio * VIEW_W;

    // Nearest point rather than the one under the cursor: with irregular
    // spacing, "under the cursor" is often nothing at all.
    let best = 0;
    let bestDistance = Infinity;
    model.scaled.forEach((p, i) => {
      const distance = Math.abs(p.x - target);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    setHover(best);
  };

  const referenceY =
    reference && model.top > 0 ? 100 - (reference.value / model.top) * 100 : null;

  return (
    <div className={cn('relative', className)}>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-xs font-medium text-muted-foreground">{title}</span>
        <span className="text-2xs tabular-nums text-muted-foreground">
          {model.gapCount > 0 ? (
            <span className="mr-2 not-italic">
              {model.gapCount} break{model.gapCount === 1 ? '' : 's'} in the record
            </span>
          ) : null}
          peak {format(Math.max(...points.map((p) => p.value)))} {unit}
        </span>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} 100`}
        preserveAspectRatio="none"
        style={{ height }}
        className="w-full touch-none"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label={title}
      >
        <defs>
          <linearGradient id={`trend-${tone}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {referenceY !== null ? (
          <line
            x1="0"
            x2={VIEW_W}
            y1={referenceY}
            y2={referenceY}
            className="stroke-muted-foreground/45"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}

        {model.areas.map((d, i) => (
          <path key={`area-${i}`} d={d} className={fill} fill={`url(#trend-${tone})`} />
        ))}
        {model.lines.map((d, i) => (
          <path
            key={`line-${i}`}
            d={d}
            fill="none"
            className={stroke}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {active ? (
          <>
            <line
              x1={active.x}
              x2={active.x}
              y1="0"
              y2="100"
              className="stroke-muted-foreground/50"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            {/* A 2px surface ring so the marker stays visible wherever it lands
                on the fill. */}
            <circle
              cx={active.x}
              cy={active.y}
              r="4"
              className={cn(fill, 'fill-current stroke-card')}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </>
        ) : null}
      </svg>

      <div className="mt-1 flex items-center justify-between text-2xs tabular-nums text-muted-foreground">
        <span>{points[0]?.label}</span>
        {reference ? (
          <span>
            {reference.label} {format(reference.value)} {unit}
          </span>
        ) : null}
        <span>{points[points.length - 1]?.label}</span>
      </div>

      {active ? (
        <div
          role="tooltip"
          className="pointer-events-none absolute top-4 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg border border-border bg-card px-2 py-1 text-2xs shadow-lg"
          style={{ left: `${Math.min(90, Math.max(10, active.x))}%` }}
        >
          <span className="font-semibold tabular-nums">
            {format(active.value)} {unit}
          </span>
          <span className="ml-1.5 text-muted-foreground">{active.label}</span>
        </div>
      ) : null}
    </div>
  );
}
